import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaHandsHelping, FaLeaf, FaCheck, FaPlay, FaClock, FaUsers, FaShieldAlt, FaLightbulb, FaCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import cuppingImage from '../../components/assets/Staff/Cupping 1.png';
import cupping2Image from '../../components/assets/Staff/Cupping 2.jpg';

const CuppingTherapy = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const sessionRef = useRef(null);
  const benefitsRef = useRef(null);
  const techniquesRef = useRef(null);
  const contraindicationsRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'session': sessionRef,
      'benefits': benefitsRef,
      'techniques': techniquesRef,
      'contraindications': contraindicationsRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'session', ref: sessionRef },
        { id: 'benefits', ref: benefitsRef },
        { id: 'techniques', ref: techniquesRef },
        { id: 'contraindications', ref: contraindicationsRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'session', label: 'What to Expect', icon: FaPlay },
    { id: 'techniques', label: 'Techniques', icon: FaHandsHelping },
    { id: 'benefits', label: 'Benefits', icon: FaLeaf },
    { id: 'contraindications', label: 'Is This For You', icon: FaShieldAlt }
  ];

  const overviewFeatures = [
    {
      icon: FaHeart,
      title: "Time-Honored Practice",
      description: "Rooted in ancient traditions and adapted to modern holistic care"
    },
    {
      icon: FaCircle,
      title: "Gentle Invitation",
      description: "Back into flow, ease, and embodied presence"
    },
    {
      icon: FaLeaf,
      title: "Listening, Softening, Allowing",
      description: "Not about fixing or forcing – deep relaxation and reconnection with your body's natural rhythms"
    }
  ];

  const cuppingTechniques = [
    {
      name: "Dry Cupping",
      description: "Gentle suction without incisions, often used to ease areas of tension and invite circulation."
    },
    {
      name: "Wet Cupping (Hijama)",
      description: "Involves light, superficial skin incisions and is traditionally associated with releasing stagnation and restoring energetic balance."
    },
    {
      name: "Fire Cupping",
      description: "Uses warmth to create suction, offering a deeply relaxing and grounding experience, especially in areas of long-held tightness."
    },
    {
      name: "Vacuum Cupping",
      description: "A modern method with adjustable pressure, combining precision with comfort."
    }
  ];

  const sessionBenefits = [
    "A sense of physical lightness and ease",
    "Release of muscular tightness and stiffness",
    "Improved circulation and tissue hydration",
    "A calmer nervous system and reduced stress response",
    "Greater mobility, posture awareness, and body connection",
    "A deeper feeling of balance — physically and emotionally"
  ];

  const whenPeopleChoose = [
    "Overworked or physically strained",
    "Tight or restricted in certain areas",
    "Heavy, stiff, or lacking ease of movement",
    "Affected by long periods of sitting, training, or repetitive movement",
    "Overwhelmed by stress that is held in the body"
  ];

  const targetAudience = [
    "Feel tension, heaviness, or stagnation in your body",
    "Carry stress physically or emotionally",
    "Are drawn to natural, intuitive forms of self-care",
    "Wish to complement massage, acupuncture, or other bodywork",
    "Value presence, intention, and a slower pace of holistic work"
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Cupping Therapy" description="Cupping therapy at Bioresonance Leicester – gentle suction for circulation and comfort. Traditional practice at 108 New Walk." path="/therapy/cupping-therapy" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">Cupping</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHeart className="mr-2" />
                A conscious pause for your body, breath, and inner flow
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                Cupping
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed">
                Do you feel tension lingering in your body — even when life finally slows down? A sense of heaviness, tightness, or stagnation that words can't quite explain?
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Cupping is a time-honored wellness practice that offers a gentle invitation back into flow, ease, and embodied presence. Rooted in ancient traditions and adapted to modern holistic care, this work creates space for deep relaxation, subtle release, and reconnection with your body's natural rhythms.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                This is not about fixing or forcing — but about listening, softening, and allowing.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>30-60 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container - same photo as Cupping card */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={cuppingImage}
                      alt="Cupping therapy"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What Makes Cupping Special
              </h2>
              <p className="text-lg text-gray-600">
                A gentle invitation back into flow, ease, and embodied presence – listening, softening, and allowing
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                >
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-2xl text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Awareness Disclaimer */}
            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaLightbulb className="text-xl text-gold" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    Not about fixing or forcing
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    This approach is chosen as a form of supportive bodywork and is not intended as a medical treatment. 
                    It offers a conscious pause — a moment to reconnect, reset, and return to yourself.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Session Experience */}
      <section ref={sessionRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                  What to expect during a session
                </h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    During your session, cups are placed on selected areas of the body, creating gentle suction that lifts the tissues and encourages circulation. This sensation is often described as warm, grounding, and deeply calming.
                  </p>
                  <p>
                    As the body responds, many people notice a gradual unwinding — a sense of slowing down, breathing more freely, and settling into stillness. For some, the experience is accompanied by emotional spaciousness or a quiet clarity that continues beyond the session itself.
                  </p>
                  <p>
                    Temporary circular marks may appear and usually fade naturally over time. They are simply part of the body's visible response to this traditional practice.
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl h-80">
                    <img
                      src={cupping2Image}
                      alt="Cupping session experience"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cupping Techniques */}
      <section ref={techniquesRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Techniques used with intention
              </h2>
              <p className="text-lg text-gray-600">
                Each session is guided intuitively and may include one or more approaches, chosen to support your comfort and experience.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {cuppingTechniques.map((technique, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                >
                  <h3 className="text-2xl font-bold text-dark-grey mb-4">{technique.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{technique.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section ref={benefitsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                How cupping may support your wellbeing
              </h2>
              <p className="text-lg text-gray-600">
                Each body responds in its own way. Clients often choose cupping as part of their self-care because it may support:
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="space-y-4 mb-6">
                  {sessionBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{benefit}</p>
                    </motion.div>
                  ))}
                </div>
                <p className="text-gray-600 italic">These experiences are subtle, personal, and unfold differently for everyone.</p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark-grey mb-6">When people often choose cupping</h3>
                <p className="text-gray-600 mb-6">Clients often turn to cupping when their body feels:</p>
                <div className="space-y-4">
                  {whenPeopleChoose.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-6 h-6 bg-blue/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaUsers className="text-blue text-sm" />
                      </div>
                      <p className="text-gray-600">{item}</p>
                    </motion.div>
                  ))}
                </div>
                <p className="text-gray-600 mt-6">This approach is chosen as a form of supportive bodywork and is not intended as a medical treatment.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-gradient-to-r from-gold/5 via-blue/5 to-gold/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              A mindful addition to your wellness journey
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Cupping can be enjoyed as a stand-alone session or integrated into a broader bodywork practice. When combined with massage, breathwork, or subtle energy approaches, many clients find the sense of release and grounding deepens naturally over time.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              This work offers a conscious pause — a moment to reconnect, reset, and return to yourself.
            </p>
          </div>
        </div>
      </section>

      {/* Is This Experience For You */}
      <section ref={contraindicationsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaShieldAlt className="mr-2" />
                Is this experience for you?
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Is this experience for you?
              </h2>
              <p className="text-lg text-gray-600">
                Cupping may resonate with you if you:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <div className="space-y-3 mb-6">
                  {targetAudience.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-xs" />
                      </div>
                      <p className="text-gray-600 text-sm">{item}</p>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 font-semibold">No prior experience is needed — only curiosity and openness.</p>
              </div>

              <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20 flex flex-col justify-center">
                <p className="text-dark-grey text-lg leading-relaxed mb-6">
                  If you feel called to explore cupping as part of your personal wellness journey, you are warmly invited to book a session or reach out with any questions.
                </p>
                <p className="text-dark-grey font-semibold text-xl" style={{ fontFamily: "Playfair Display, serif" }}>
                  Your body already knows the way.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Explore Cupping?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            A conscious pause for your body, breath, and inner flow. If you feel called to explore cupping as part of your wellness journey, you are warmly invited to book a session. Your body already knows the way.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Your Session
            </a>
            <Link
              to="/prices"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default CuppingTherapy;
